import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { useTranslation } from 'react-i18next';

import type { NodeViewProps } from '@tiptap/core';

function VariableChipNodeView({ node }: NodeViewProps) {
  const { t } = useTranslation();
  return (
    <NodeViewWrapper as='span' className='inline-block align-middle' data-drag-handle>
      <span
        contentEditable={false}
        aria-label={t('blocks.toolbar.variableChipLabel', { name: node.attrs.name })}
        title={t('blocks.toolbar.variableChipLabel', { name: node.attrs.name })}
        className='ws-var-chip ws-var-chip-draggable'
      >
        {'{' + node.attrs.name + '}'}
      </span>
    </NodeViewWrapper>
  );
}

export const VariableChip = Node.create({
  name: 'variableChip',
  group: 'inline',
  inline: true,
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      name: {
        default: '',
        parseHTML: el => el.getAttribute('data-variable-chip') ?? '',
        renderHTML: attrs => ({ 'data-variable-chip': attrs.name }),
      },
      /**
       * The variable's stable id, so a rename can target identity instead of matching text.
       *
       * Renaming used to be a search-and-replace on `data-variable-chip="oldName"` across every
       * block, which cannot distinguish two variables that ever share a name — and one briefly did
       * every time a new name was typed past an existing one, silently rewriting the other
       * variable's chips.
       *
       * Optional by design: chips authored before this attribute existed have no id, and
       * renameVarInHtml falls back to name matching for them. That keeps old content working with
       * no migration, and every chip inserted from now on is unambiguous.
       */
      varId: {
        default: null,
        parseHTML: el => el.getAttribute('data-variable-id'),
        renderHTML: attrs => (attrs.varId ? { 'data-variable-id': attrs.varId } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-variable-chip]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), HTMLAttributes['data-variable-chip'] ?? ''];
  },

  /**
   * What a chip becomes when it leaves rich text: `{name}`, braces and all.
   *
   * Without this, TipTap serialises an atom node to its bare text content — so copying a chip and
   * pasting it anywhere that is not a rich-text field produced `x` rather than `{x}`. That silently
   * stops being a variable reference: `{x}` is the authoring syntax the whole system round-trips on
   * (`htmlWithChips` turns `{name}` back into a chip, and stripChips already writes `{name}` when
   * flattening HTML), while a naked `x` is just a letter.
   *
   * It also settles what happens when a chip is pasted somewhere variables are FORBIDDEN. Every
   * answer, tolerance and trigger field is a plain numeric `<input>` with no toolbar, so a chip
   * cannot be inserted there — and a pasted one arrives as this plain text, which the numeric
   * filters (filterPositiveNumber, filterDecimalText) then strip. The field is left empty rather
   * than holding a variable it cannot evaluate, and no chip formatting can survive the crossing.
   */
  renderText({ node }) {
    return `{${node.attrs.name}}`;
  },

  /**
   * ONE PRESS TO DELETE A CHIP, not two.
   *
   * An `atom` node is selectable, and ProseMirror's default Backspace beside one SELECTS it and
   * waits for a second press. For a text caret that reads as a dead keystroke: the first press
   * appears to do nothing — the chip's selection ring is easy to miss inside a coloured chip —
   * and the teacher presses again. Reported exactly that way.
   *
   * Only when the caret sits immediately beside the chip with nothing selected. Everything else,
   * including Backspace over a chip that IS already selected, falls through to the default.
   */
  addKeyboardShortcuts() {
    const removeAdjacent = (side: 'before' | 'after') => () => {
      const { state } = this.editor;
      const { empty, $anchor } = state.selection;
      if (!empty) return false;
      const node = side === 'before' ? $anchor.nodeBefore : $anchor.nodeAfter;
      if (node?.type.name !== this.name) return false;
      const from = side === 'before' ? $anchor.pos - node.nodeSize : $anchor.pos;
      return this.editor.commands.deleteRange({ from, to: from + node.nodeSize });
    };
    return { Backspace: removeAdjacent('before'), Delete: removeAdjacent('after') };
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableChipNodeView);
  },
});
